import {
  Square2StackIcon,
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  XCircleIcon,
  CloudArrowDownIcon,
  ArrowDownTrayIcon,
  LinkIcon,
  KeyIcon,
  DocumentDuplicateIcon,
  ClipboardDocumentIcon,
  BugAntIcon,
  LockClosedIcon,
  LockOpenIcon,
} from '@heroicons/react/24/outline';
import { ListItem } from '../../../components/ListItem';
import { StatusPill } from '../../../components/Pill';
import { Popover } from '../../../components/Popover';
import { FavoriteIcon } from '../../../components/favoriteIcon';
import { UserContext } from '../../../contexts/UserContext';
import { CURRENT_USER } from '../../../graphql/user';
import { useRecentProjects } from '../../../hooks/modules/project/useRecentProjects';
import { triggerUrlDownload } from '../../../hooks/useDownload';
import useOpen from '../../../hooks/useOpen';
import { ToastType, useToastNotifications } from '../../../hooks/useToastNotifications';
import { T, useT } from '../../../translation/src';
import {
  GeneralStatus,
  PointCloudValidation,
  Project,
  ProjectCopyType,
  ProjectRelaunchMutation,
  UploadStatus,
  User,
  useIsLocalStorageEnabledQuery,
  useProjectRelaunchMutation,
  useProjectValidatePointCloudMutation,
  useSuperadminProjectSourceFilesLazyQuery,
  useUserUpdateFavoritesMutation,
} from '../../../types/graphqlTypes';
import classNames from 'classnames';
import { MouseEventHandler, memo, useCallback, useContext, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DotsPopover } from '../../../components/Popover/DotsPopover';
import { ProjectDeleteModal } from '../ProjectDeleteModal';
import { ProjectDuplicateModal } from '../ProjectDuplicateModal';
import { ProjectCloneFromCloudModal } from '../ProjectCloneFromCloudModal';
import { ProjectCopyToOrganisationModal } from '../ProjectCopyToOrganisationModal';
import { ProjectModal } from '../ProjectModal';
import { ProjectProcessModal } from '../ProjectProcessModal';
import { ProjectSuspendedModal } from '../ProjectSuspendedModal';
import { ChargeCreditsModal } from '../ChargeCreditsModal';
import { ProjectDebugStateModal } from '../ProjectDebugStateModal';
import { useShowDebugUi } from '../../Properties/integration/UserSettingsIntegration';
import { ProjectShareModal } from '../ProjectShareModal';
import { ProjectAccessOverviewModal } from '../ProjectAccessOverviewModal';

const FavoriteToggle = memo(({ projectId, isFavorite }: { projectId: string; isFavorite: boolean }) => {
  const [updateFavoriteProjects] = useUserUpdateFavoritesMutation();
  const onClick: MouseEventHandler<HTMLDivElement> = useCallback(async () => {
    await updateFavoriteProjects({
      variables: { projectId, inFavorites: !isFavorite },
      refetchQueries: [{ query: CURRENT_USER }],
      awaitRefetchQueries: true,
    });
  }, [isFavorite, projectId, updateFavoriteProjects]);
  return <FavoriteIcon onClick={onClick} className="absolute left-3 top-3" checked={isFavorite} />;
});
FavoriteToggle.displayName = 'FavoriteToggle';

const RelaunchMenuItem = memo(({ projectId, superadminOnly }: { projectId: string; superadminOnly: boolean }) => {
  const [relaunchProject] = useProjectRelaunchMutation();
  const onRelaunch = useCallback(
    () =>
      relaunchProject({
        variables: { projectId },
        update: (cache, { data }: { data?: ProjectRelaunchMutation | null }) => {
          if (!data?.projectRelaunch) return;
          cache.modify({
            id: `Project:${projectId}`,
            fields: {
              uploadStatus: () => data.projectRelaunch.uploadStatus,
              isInitializing: () => data.projectRelaunch.isInitializing,
            },
          });
        },
      }),
    [relaunchProject, projectId],
  );
  return (
    <ListItem onClick={onRelaunch} icon={ArrowPathIcon} superadminOnly={superadminOnly}>
      <T _str="relaunch import" swc />
    </ListItem>
  );
});
RelaunchMenuItem.displayName = 'RelaunchMenuItem';

const ValidateMenuItem = memo(({ projectId, pointClouds }: { projectId: string; pointClouds: { cloudName?: string | null }[] }) => {
  const [validatePointCloud] = useProjectValidatePointCloudMutation();
  const onValidate = useCallback(async () => {
    for (const pc of pointClouds) {
      if (!pc.cloudName) continue;
      await validatePointCloud({ variables: { projectId, cloudName: pc.cloudName } });
    }
  }, [validatePointCloud, projectId, pointClouds]);
  return (
    <ListItem onClick={onValidate} icon={ShieldCheckIcon} superadminOnly>
      <T _str="validate pointclouds" swc />
    </ListItem>
  );
});
ValidateMenuItem.displayName = 'ValidateMenuItem';

// Superadmin: everything uploaded for the project, straight from its storage — also when no point cloud
// entry points at it (a relaunch cleared them) or the upload never became one (a zip).
const DownloadSourceMenuItem = memo(({ projectId }: { projectId: string }) => {
  const [loadSourceFiles] = useSuperadminProjectSourceFilesLazyQuery({ fetchPolicy: 'network-only' });
  const { showToast } = useToastNotifications();
  const noFiles = useT('no source files in storage', { swc: true });
  const failed = useT('could not download the source', { swc: true });
  const onDownload = useCallback(async () => {
    try {
      const { data } = await loadSourceFiles({ variables: { projectId } });
      const files = data?.superadminProjectSourceFiles ?? [];
      if (files.length === 0) {
        showToast({ title: noFiles, text: projectId, type: ToastType.WARNING });
        return;
      }
      // Staggered: browsers drop downloads fired in the same tick. The loop lives in this closure, so it
      // keeps running after the popover closes on click.
      for (let i = 0; i < files.length; i++) {
        triggerUrlDownload(files[i].url);
        if (i < files.length - 1) await new Promise((resolve) => setTimeout(resolve, 500));
      }
    } catch (e) {
      showToast({ title: failed, text: e instanceof Error ? e.message : String(e), type: ToastType.ERROR });
    }
  }, [loadSourceFiles, projectId, showToast, noFiles, failed]);
  return (
    <ListItem onClick={onDownload} icon={ArrowDownTrayIcon} superadminOnly>
      <T _str="Download Source" swc />
    </ListItem>
  );
});
DownloadSourceMenuItem.displayName = 'DownloadSourceMenuItem';

// Superadmin: the project id on the clipboard, for support and scripts.
const CopyProjectIdMenuItem = memo(({ projectId }: { projectId: string }) => {
  const { showToast } = useToastNotifications();
  const copied = useT('project ID copied', { swc: true });
  const copyFailed = useT('could not copy the project ID', { swc: true });
  const onCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(projectId);
      showToast({ title: copied, text: projectId, type: ToastType.SUCCESS });
    } catch {
      showToast({ title: copyFailed, text: projectId, type: ToastType.ERROR });
    }
  }, [projectId, showToast, copied, copyFailed]);
  return (
    <ListItem onClick={onCopy} icon={ClipboardDocumentIcon} superadminOnly>
      <T _str="copy project ID" swc />
    </ListItem>
  );
});
CopyProjectIdMenuItem.displayName = 'CopyProjectIdMenuItem';

// Superadmin tools only for debugging: in the menu while "show debug ui" is on (user settings), hidden otherwise.
// A component of its own so the setting is only read when a menu is open, not by every card.
const DebugMenuItems = memo(({ projectId, validatePointClouds, onDebugState }: { projectId: string; validatePointClouds?: { cloudName?: string | null }[]; onDebugState: MouseEventHandler<HTMLButtonElement> }) => {
  const { showDebugUi } = useShowDebugUi();
  if (!showDebugUi) return null;
  return (
    <>
      {validatePointClouds && <ValidateMenuItem projectId={projectId} pointClouds={validatePointClouds} />}
      <CopyProjectIdMenuItem projectId={projectId} />
      <ListItem onClick={onDebugState} icon={BugAntIcon} superadminOnly>
        <T _str="debug project state" swc />
      </ListItem>
    </>
  );
});
DebugMenuItems.displayName = 'DebugMenuItems';

// What an AWAITING_CREDITS project asks for, on the card itself: a way in, not a warning. The whole card opens
// the activation dialog; this only says so, and lights up with the card.
// A badge on the preview image: one size for all of them, whatever they hold.
const PreviewBadge: React.FC<{ className: string; children: React.ReactNode }> = ({ className, children }) => (
  <div className={classNames('flex h-6 items-center gap-1 whitespace-nowrap rounded-md px-2 text-xs leading-none text-white', className)}>{children}</div>
);

const ActivatePill = memo(({ projects, affordable }: { projects: number; affordable: boolean }) => (
  <span
    className={classNames(
      'flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors group-hover:text-black',
      // Green: the balance covers it, one click away. Orange: projects have to be bought first (or the subscription resumes).
      affordable ? 'border-neon-green-500 text-neon-green-700 group-hover:bg-neon-green-500 dark:text-neon-green-300' : 'border-amber-500 text-amber-700 group-hover:bg-amber-500 dark:text-amber-400',
    )}
  >
    <LockOpenIcon className="h-3.5 w-3.5" />
    {projects === 1 ? <T _str="1 project" /> : <T _str="{count} projects" args={{ count: projects }} />}
  </span>
));
ActivatePill.displayName = 'ActivatePill';

const ProjectCard_: React.FC2<{
  project: Pick<Project, 'name' | 'uploadStatus' | 'creditsOutstanding' | 'creditsWaived' | 'creditBreakdown' | 'id' | 'previewUrl' | 'isLocal'> & {
    assignee?: Pick<User, 'id' | 'name'> | null;
    copy?: { type: ProjectCopyType } | null;
    pointClouds: Array<{
      id: string;
      pointCount?: number | null;
      cloudName?: string | null;
      validation?: Pick<PointCloudValidation, 'status' | 'isValid' | 'finishedAt'> | null;
    }>;
  };
  isReadOnly?: boolean;
  // A partner's admin on a referred organisation: the card is a row of the list, not a way into the project,
  // and its only actions are a relaunch (of a failed import) and a soft copy into the partner's own organisation.
  partnerScoped?: boolean;
  /** Projects the organisation can spend right now (0 while its subscription is paused); unknown: shown as affordable. */
  projectsAvailable?: number;
}> = ({ className, project, isReadOnly, partnerScoped, projectsAvailable }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { open: projectModalOpen, onClose: closeProjectModal, onOpen: openProjectModal } = useOpen(false, { stopPropagationOnClick: true });
  const { open: deleteModalOpen, onClose: closeDeleteModal, onOpen: openDeleteModal } = useOpen(false, { stopPropagationOnClick: true });
  const { open: duplicateModalOpen, onClose: closeDuplicateModal, onOpen: openDuplicateModal } = useOpen(false, { stopPropagationOnClick: true });
  const { open: processModalOpen, onClose: closeProcessModal, onOpen: openProcessModal } = useOpen(false, { stopPropagationOnClick: true });
  const { open: suspendedModalOpen, onClose: closeSuspendedModal, onOpen: openSuspendedModal } = useOpen(false, { stopPropagationOnClick: true });
  const { open: chargeCreditsModalOpen, onClose: closeChargeCreditsModal, onOpen: openChargeCreditsModal } = useOpen(false, { stopPropagationOnClick: true });
  const { open: shareModalOpen, onClose: closeShareModal, onOpen: openShareModal } = useOpen(false, { stopPropagationOnClick: true });
  const { open: accessModalOpen, onClose: closeAccessModal, onOpen: openAccessModal } = useOpen(false, { stopPropagationOnClick: true });
  const { open: cloneFromCloudModalOpen, onClose: closeCloneFromCloudModal, onOpen: openCloneFromCloudModal } = useOpen(false, { stopPropagationOnClick: true });
  const { open: copyToOrganisationModalOpen, onClose: closeCopyToOrganisationModal, onOpen: openCopyToOrganisationModal } = useOpen(false, { stopPropagationOnClick: true });
  const { open: debugStateModalOpen, onClose: closeDebugStateModal, onOpen: openDebugStateModal } = useOpen(false, { stopPropagationOnClick: true });
  const { 1: addRecentProject } = useRecentProjects();

  // Defer mounting modals until first open so cold renders (e.g. org switch with many cards)
  // don't pay for 6× modal subtrees per card. Once mounted, modals stay mounted so Headless UI
  // exit transitions still play.
  const [projectModalMounted, setProjectModalMounted] = useState(false);
  const [deleteModalMounted, setDeleteModalMounted] = useState(false);
  const [duplicateModalMounted, setDuplicateModalMounted] = useState(false);
  const [processModalMounted, setProcessModalMounted] = useState(false);
  const [suspendedModalMounted, setSuspendedModalMounted] = useState(false);
  const [chargeCreditsModalMounted, setChargeCreditsModalMounted] = useState(false);
  const [shareModalMounted, setShareModalMounted] = useState(false);
  const [accessModalMounted, setAccessModalMounted] = useState(false);
  const [cloneFromCloudModalMounted, setCloneFromCloudModalMounted] = useState(false);
  const [copyToOrganisationModalMounted, setCopyToOrganisationModalMounted] = useState(false);
  if (projectModalOpen && !projectModalMounted) setProjectModalMounted(true);
  if (deleteModalOpen && !deleteModalMounted) setDeleteModalMounted(true);
  if (duplicateModalOpen && !duplicateModalMounted) setDuplicateModalMounted(true);
  if (processModalOpen && !processModalMounted) setProcessModalMounted(true);
  if (suspendedModalOpen && !suspendedModalMounted) setSuspendedModalMounted(true);
  if (chargeCreditsModalOpen && !chargeCreditsModalMounted) setChargeCreditsModalMounted(true);
  if (shareModalOpen && !shareModalMounted) setShareModalMounted(true);
  if (accessModalOpen && !accessModalMounted) setAccessModalMounted(true);
  if (cloneFromCloudModalOpen && !cloneFromCloudModalMounted) setCloneFromCloudModalMounted(true);
  if (copyToOrganisationModalOpen && !copyToOrganisationModalMounted) setCopyToOrganisationModalMounted(true);

  const onMouseDownProject: MouseEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (partnerScoped) return;
      if ((event.target as HTMLElement).id?.includes('popover')) return;
      if (event.button !== 0 && event.button !== 1) return;
      if (project.uploadStatus === UploadStatus.Error) return;
      if (project.uploadStatus === UploadStatus.Progress) return openProcessModal(event);
      if (project.uploadStatus === UploadStatus.Suspended) return openSuspendedModal(event);
      if (project.uploadStatus === UploadStatus.AwaitingCredits) return openChargeCreditsModal(event);
      addRecentProject(project.id);
      const path = `${location.pathname.includes('project') ? location.pathname : `${location.pathname}/projects`}/${project.id}`;
      const openInNewTab = event.button === 1 || (event.button === 0 && (event.ctrlKey || event.metaKey));
      if (openInNewTab) {
        const w = window.open(`${window.location.origin}${path}`, '_blank');
        w?.focus();
      } else {
        navigate(path);
      }
    },
    [addRecentProject, location.pathname, navigate, openProcessModal, openSuspendedModal, openChargeCreditsModal, partnerScoped, project.id, project.uploadStatus],
  );

  const currentUser = useContext(UserContext);
  const isFavorite = currentUser.favoriteProjectIds.includes(project.id);
  // Apollo caches the result, so this is effectively one network call per app session no matter
  // how many project cards mount. Skip when the user isn't a super-admin since only they see the menu item.
  const { data: localStorageData } = useIsLocalStorageEnabledQuery({ skip: !currentUser.isSuperAdmin });
  const localStorageEnabled = !!localStorageData?.isLocalStorageEnabled;

  // Stabilise the ValidateMenuItem props so its memo can skip re-renders.
  const validatePointClouds = useMemo(() => project.pointClouds.map((pc) => ({ cloudName: pc.cloudName })), [project.pointClouds]);

  // Aggregate the latest validation across this project's pointclouds:
  // - in PROGRESS if any pointcloud is still validating
  // - ERROR if any finished validation failed (isValid === false or status === ERROR)
  // - SUCCESS if all known validations passed
  // - null when nothing has been validated yet
  // Detailed per-issue messages are intentionally NOT fetched in PROJECTS_FRAGMENT (they live
  // inside processes[].result and would bloat the org-wide list query). Open the project for the
  // full breakdown.
  const aggregateValidation = ((): { status: GeneralStatus; isValid: boolean | null; lines: { cloudName: string; isValid: boolean | null; status: GeneralStatus }[] } | null => {
    const items = project.pointClouds
      .map((pc) => (pc.validation ? { cloudName: pc.cloudName || pc.id, validation: pc.validation } : null))
      .filter((item): item is { cloudName: string; validation: NonNullable<(typeof project.pointClouds)[number]['validation']> } => item !== null);
    if (items.length === 0) return null;
    const lines = items.map(({ cloudName, validation }) => ({
      cloudName,
      isValid: validation.isValid ?? null,
      status: validation.status,
    }));
    if (items.some(({ validation }) => validation.status === GeneralStatus.Progress)) return { status: GeneralStatus.Progress, isValid: null, lines };
    const anyInvalid = items.some(({ validation }) => validation.status === GeneralStatus.Error || validation.isValid === false);
    if (anyInvalid) return { status: GeneralStatus.Error, isValid: false, lines };
    return { status: GeneralStatus.Success, isValid: true, lines };
  })();

  return (
    <>
      <div className="relative">
        <div
          className={classNames(
            project.uploadStatus !== UploadStatus.Error && !partnerScoped && 'cursor-pointer hover:bg-gray-200 hover:border-neon-green-400 dark:hover:border-neon-green-400 dark:hover:bg-black',
            'group flex flex-col w-full h-56 shadow-md bg-white hover:shadow-lg transition-shadow border border-gray-200 rounded-xl overflow-hidden',
            'dark:bg-[#242424] dark:border-gray-500',
            className,
          )}
          onMouseDown={onMouseDownProject}
        >
          <div className="relative flex items-center justify-center w-full text-white bg-gradient-to-br from-eerie-black to-ash-green-800 h-36">
            {project.previewUrl && <img src={project.previewUrl} className="object-cover w-full h-36" alt="pointcloud_cover" crossOrigin="anonymous" loading="lazy" decoding="async" />}
            {project.uploadStatus === UploadStatus.Progress && (
              <div className="absolute -translate-x-1/2 left-1/2 top-1/2">
                <ArrowPathIcon className="w-6 h-6 text-white" />
              </div>
            )}
            {/* Badges on the preview, side by side when there are several. */}
            {(project.isLocal || project.uploadStatus === UploadStatus.AwaitingCredits) && (
              <div className="absolute bottom-2 right-2 flex gap-1.5">
                {project.uploadStatus === UploadStatus.AwaitingCredits && (
                  // Neutral on purpose: a state, not an alarm. The footer pill says what it takes.
                  <PreviewBadge className="bg-black/60 backdrop-blur-sm">
                    <LockClosedIcon className="h-3.5 w-3.5" />
                    <T _str="awaiting activation" swc />
                  </PreviewBadge>
                )}
                {project.isLocal && (
                  <PreviewBadge className="bg-gradient-to-r from-blue-600 to-blue-400">
                    <T _str="local" swc />
                  </PreviewBadge>
                )}
              </div>
            )}
            {/* Both share their source's data. A soft copy sits in another organisation and goes when the owner deletes theirs. */}
            {project.copy && (
              <div
                className={classNames(
                  'absolute bottom-2 left-2 flex py-1 px-2 rounded-md justify-center items-center text-xs text-white bg-gradient-to-r',
                  project.copy.type === ProjectCopyType.Copy ? 'from-purple-600 to-purple-400' : 'from-gray-600 to-gray-400',
                )}
              >
                {project.copy.type === ProjectCopyType.Copy ? <T _str="soft copy" swc /> : <T _str="duplicate" swc />}
              </div>
            )}
            {aggregateValidation &&
              (aggregateValidation.status === GeneralStatus.Progress ? (
                <ArrowPathIcon className="absolute top-2 right-11 w-6 h-6 text-white animate-spin" />
              ) : (
                <Popover
                  className="absolute top-2 right-11"
                  panelClassName="w-72 max-h-80 overflow-auto"
                  buttonChild={aggregateValidation.isValid ? <CheckCircleIcon className="w-6 h-6 text-neon-green-400" /> : <XCircleIcon className="w-6 h-6 text-red-500" />}
                >
                  <div className="flex flex-col gap-2 text-xs" onMouseDown={(e) => e.stopPropagation()}>
                    <span className="font-semibold">
                      <T _str="validation result" swc />
                    </span>
                    {aggregateValidation.lines.map(({ cloudName, isValid }) => (
                      <div key={cloudName} className="flex items-start gap-2">
                        {isValid ? <CheckCircleIcon className="w-4 h-4 text-neon-green-400 shrink-0 mt-0.5" /> : <XCircleIcon className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />}
                        <span className="font-mono text-[11px] break-all">{cloudName}</span>
                      </div>
                    ))}
                    <div className="text-[11px] text-gray-500 dark:text-gray-400">
                      <T _str="open the project for full details" swc />
                    </div>
                  </div>
                </Popover>
              ))}
          </div>
          <div className="flex flex-col px-4 py-4">
            <div className="truncate">{project.name}</div>
            <div className="flex items-center justify-between gap-2">
              <span className="min-w-0 truncate text-xs">
                <span className="font-light text-gray-400">
                  <T _str="uploaded by" swc />
                </span>{' '}
                <span>{project.assignee?.name || '-'}</span>
              </span>
              {project.uploadStatus === UploadStatus.AwaitingCredits ? (
                <ActivatePill projects={project.creditsOutstanding || project.creditBreakdown.required} affordable={projectsAvailable === undefined || projectsAvailable >= Math.max(1, project.creditsOutstanding)} />
              ) : (
                project.uploadStatus !== UploadStatus.Success && <StatusPill status={project.uploadStatus.toLocaleLowerCase()} />
              )}
            </div>
          </div>
        </div>
        <FavoriteToggle projectId={project.id} isFavorite={isFavorite} />
        {/* A partner may only rebuild what failed, and soft-copy what succeeded into its own organisation. */}
        {partnerScoped && (project.uploadStatus === UploadStatus.Error || project.uploadStatus === UploadStatus.Success) && (
          <DotsPopover data-project-actions panelClassName="mt-10">
            {({ open }) => {
              if (!open) return null;
              if (project.uploadStatus === UploadStatus.Error) return <RelaunchMenuItem projectId={project.id} superadminOnly={false} />;
              return (
                <ListItem onClick={openCopyToOrganisationModal} icon={DocumentDuplicateIcon}>
                  <T _str="soft copy" swc />
                </ListItem>
              );
            }}
          </DotsPopover>
        )}
        {!isReadOnly && !partnerScoped && (
          <DotsPopover data-project-actions panelClassName={project.uploadStatus === UploadStatus.Progress ? 'w-72 mt-10' : 'mt-10'}>
            {({ open }) => {
              // Menu items only mount while it is open, so the relaunch/validate mutation hooks (and any other heavy
              // menu content) don't initialise on cold render of every card.
              if (!open) return null;
              return project.uploadStatus !== UploadStatus.Progress ? (
                <>
                  <ListItem onClick={openProjectModal} icon={PencilIcon}>
                    <T _str="edit information" swc />
                  </ListItem>
                  <ListItem onClick={openAccessModal} icon={KeyIcon}>
                    <T _str="manage access" swc />
                  </ListItem>
                  <ListItem onClick={openShareModal} icon={LinkIcon}>
                    <T _str="share or invite" swc />
                  </ListItem>
                  <hr className="my-2 border-t border-gray-300" />
                  {project.uploadStatus === UploadStatus.Success && (
                    <ListItem onClick={openDuplicateModal} icon={Square2StackIcon}>
                      <T _str="duplicate" swc />
                    </ListItem>
                  )}
                  {currentUser.isSuperAdmin && project.uploadStatus === UploadStatus.Success && (
                    <ListItem onClick={openCopyToOrganisationModal} icon={DocumentDuplicateIcon} superadminOnly>
                      <T _str="soft copy" swc />
                    </ListItem>
                  )}
                  {(project.uploadStatus === UploadStatus.Error || currentUser.isSuperAdmin) && <RelaunchMenuItem projectId={project.id} superadminOnly={project.uploadStatus !== UploadStatus.Error} />}
                  {currentUser.isSuperAdmin && <DebugMenuItems projectId={project.id} validatePointClouds={project.uploadStatus === UploadStatus.Success ? validatePointClouds : undefined} onDebugState={openDebugStateModal} />}
                  {/* The uploaded source, also of a failed project: to see what went wrong with it. */}
                  {currentUser.isSuperAdmin && <DownloadSourceMenuItem projectId={project.id} />}
                  {currentUser.isSuperAdmin && !project.isLocal && localStorageEnabled && project.uploadStatus === UploadStatus.Success && (
                    <ListItem onClick={openCloneFromCloudModal} icon={CloudArrowDownIcon} superadminOnly>
                      <T _str="clone from cloud" swc />
                    </ListItem>
                  )}
                  <ListItem onClick={openDeleteModal} icon={TrashIcon}>
                    <T _str="delete" swc />
                  </ListItem>
                </>
              ) : (
                <>
                  {currentUser.isSuperAdmin && <DebugMenuItems projectId={project.id} onDebugState={openDebugStateModal} />}
                  <ListItem onClick={openDeleteModal} icon={TrashIcon}>
                    <T _str="cancel processing and delete" swc />
                  </ListItem>
                </>
              );
            }}
          </DotsPopover>
        )}
      </div>
      {projectModalMounted && <ProjectModal onClose={closeProjectModal} open={projectModalOpen} project={project} />}
      {deleteModalMounted && <ProjectDeleteModal onClose={closeDeleteModal} open={deleteModalOpen} project={project} />}
      {duplicateModalMounted && <ProjectDuplicateModal onClose={closeDuplicateModal} open={duplicateModalOpen} project={project} />}
      {cloneFromCloudModalMounted && <ProjectCloneFromCloudModal onClose={closeCloneFromCloudModal} open={cloneFromCloudModalOpen} project={project} />}
      {copyToOrganisationModalMounted && <ProjectCopyToOrganisationModal onClose={closeCopyToOrganisationModal} open={copyToOrganisationModalOpen} project={project} />}
      {processModalMounted && <ProjectProcessModal onClose={closeProcessModal} open={processModalOpen} project={project} />}
      {suspendedModalMounted && <ProjectSuspendedModal onClose={closeSuspendedModal} open={suspendedModalOpen} project={project} />}
      {chargeCreditsModalMounted && <ChargeCreditsModal onClose={closeChargeCreditsModal} open={chargeCreditsModalOpen} project={project} />}
      {shareModalMounted && <ProjectShareModal onClose={closeShareModal} open={shareModalOpen} project={project} />}
      {accessModalMounted && <ProjectAccessOverviewModal onClose={closeAccessModal} open={accessModalOpen} project={project} />}
      {/* Mounted per opening: the form starts from the project as it is now. */}
      {debugStateModalOpen && <ProjectDebugStateModal onClose={closeDebugStateModal} open={debugStateModalOpen} project={project} />}
    </>
  );
};

export const ProjectCard = memo(ProjectCard_);
